// Zenus API Service for USD Account Operations

import { getServerUrl } from '../../config/server.config';

class ZenusService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const API_URL = getServerUrl(true); // true to include /api
    const url = `${API_URL}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options?.headers
    };

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `Request failed with status ${response.status}`);
    }

    return await response.json();
  }

  // Get Zenus account information
  async getMyAccount(): Promise<any> {
    return this.request<any>('/providers/zenus/my-account', {
      method: 'GET'
    });
  }

  // Get Zenus account balance (real-time calculated from transactions)
  async getBalance(): Promise<any> {
    return this.request<any>('/providers/zenus/balance', {
      method: 'GET'
    });
  }

  // Get Zenus account status
  async getAccountStatus(): Promise<any> {
    return this.request<any>('/providers/zenus/account-status', {
      method: 'GET'
    });
  }

  // Get Zenus transactions
  async getTransactions(params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    type?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.type) queryParams.append('type', params.type);

    const queryString = queryParams.toString();
    const endpoint = `/providers/zenus/transactions${queryString ? `?${queryString}` : ''}`;

    return this.request<any>(endpoint, {
      method: 'GET'
    });
  }

  // Get deposit instructions for USD
  async getDepositInstructions(): Promise<any> {
    return this.request<any>('/providers/zenus/deposit-instructions', {
      method: 'GET'
    });
  }

  // Create a Zenus transfer
  async createTransfer(data: {
    amount: number;
    recipientAccountNumber: string;
    recipientRoutingNumber: string;
    recipientName: string;
    description?: string;
  }): Promise<any> {
    return this.request<any>('/providers/zenus/transfers', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Get dashboard data (balance, transactions summary)
  async getDashboardData(): Promise<any> {
    return this.request<any>('/providers/zenus/dashboard', {
      method: 'GET'
    });
  }

  // Search Belobaba users by email for internal transfers
  async searchUsersByEmail(email: string): Promise<any> {
    return this.request<any>(`/providers/zenus/search-users?email=${encodeURIComponent(email)}`, {
      method: 'GET'
    });
  }

  // Initialize internal Zenus transfer
  async initializeInternalTransfer(data: {
    recipientEmail?: string;
    recipientAccount?: string;
    amount: number;
    reference: string;
    details?: string;
  }): Promise<any> {
    return this.request<any>('/providers/zenus/payments/transfer', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Initialize ACH transfer
  async initializeACHTransfer(data: {
    amount: number;
    details?: string;
    endToEndId?: string;
    referenceNumber?: string;
    purposeCode?: string;
    purposeMemo?: string;
    deviceChannel?: string;
    invoiceNumber?: string;
    counterparty: {
      name: string;
      personType?: 'P' | 'L';
      firstName?: string;
      lastName?: string;
      givenName?: string;
      surname?: string;
      accountNumber: string;
      accountCountryCode?: string;
      routingNumber: string;
      address: {
        street1: string;
        street2?: string;
        city: string;
        state: string;
        zip: string;
        country?: string;
      };
      identificationType?: string;
      identificationValue?: string;
      identification?: {
        type: string;
        value: string;
      };
      bankName?: string;
      bankAddress?: string;
      bankCountry?: string;
    };
  }): Promise<any> {
    return this.request<any>('/providers/zenus/payments/ach', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Initialize SWIFT transfer
  async initializeSWIFTTransfer(data: {
    amount: number;
    details: string;
    endToEndId?: string; // Opcional - se genera en backend si no se provee
    referenceNumber?: string;
    purposeCode?: string;
    counterparty: {
      name: string;
      personType: 'P' | 'L';
      givenName?: string;
      surname?: string;
      accountNumber: string;
      accountNumberType?: 'BBAN' | 'IBAN';
      accountCountryCode: string;
      swiftCode: string;
      address: {
        street1: string;
        street2?: string;
        city: string;
        state?: string;
        zip?: string;
        country: string;
      };
      identification?: {
        type: string;
        value: string;
      };
      bankName: string;
      bankAddress: string;
      bankCountry: string;
      intermediaryBankBIC?: string;
    };
  }): Promise<any> {
    return this.request<any>('/providers/zenus/payments/swift', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Initialize FedWire transfer
  async initializeFedWireTransfer(data: {
    amount: number;
    details: string;
    endToEndId?: string;
    referenceNumber?: string;
    purposeCode?: string;
    purposeMemo?: string;
    deviceChannel?: string;
    invoiceNumber?: string;
    counterparty: {
      name: string;
      personType?: 'P' | 'L';
      firstName?: string;
      lastName?: string;
      accountNumber: string;
      routingNumber: string;
      address: {
        street1: string;
        street2?: string;
        city: string;
        state?: string;
        zip?: string;
        country: string;
      };
      identificationType?: string;
      identificationValue?: string;
      bankName?: string;
      bankAddress?: string;
      bankCountry?: string;
    };
  }): Promise<any> {
    return this.request<any>('/providers/zenus/payments/fedwire', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Confirm a payment
  async confirmPayment(paymentId: string): Promise<any> {
    return this.request<any>(`/providers/zenus/payments/${paymentId}/confirm`, {
      method: 'POST'
    });
  }

  // Cancel a payment
  async cancelPayment(paymentId: string, reason: string): Promise<any> {
    return this.request<any>(`/providers/zenus/payments/${paymentId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  }

  // Get payment details
  async getPaymentDetails(paymentId: string): Promise<any> {
    return this.request<any>(`/providers/zenus/payments/${paymentId}`, {
      method: 'GET'
    });
  }

  // Get payment history
  async getPaymentHistory(params?: { pageNumber?: number; pageSize?: number }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const queryString = queryParams.toString();
    const endpoint = `/providers/zenus/payments${queryString ? `?${queryString}` : ''}`;

    return this.request<any>(endpoint, {
      method: 'GET'
    });
  }

  // Get payment purpose codes
  async getPaymentPurposeCodes(): Promise<any> {
    return this.request<any>('/providers/zenus/payment-purposes', {
      method: 'GET'
    });
  }

  // =====================================================
  // COUNTERPARTIES (Destinatarios guardados)
  // =====================================================

  // Create a new counterparty
  async createCounterparty(data: {
    nickname: string;
    transferType: 'ACH' | 'SWIFT' | 'FEDWIRE';
    beneficiaryName: string;
    personType?: 'P' | 'L';
    givenName?: string;
    surname?: string;
    accountNumber: string;
    accountCountryCode: string;
    routingNumber?: string;
    swiftCode?: string;
    addressStreet1: string;
    addressStreet2?: string;
    addressCity: string;
    addressState?: string;
    addressZip?: string;
    addressCountry: string;
    identificationType?: string;
    identificationValue?: string;
    bankName?: string;
    bankAddress?: string;
    bankCity?: string;
    bankCountry?: string;
    intermediaryBankBIC?: string;
    intermediaryBankCountry?: string;
    reference?: string;
  }): Promise<any> {
    return this.request<any>('/providers/zenus/counterparties', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Get all counterparties for the user
  async getCounterparties(transferType?: 'ACH' | 'SWIFT' | 'FEDWIRE'): Promise<any> {
    const queryString = transferType ? `?transferType=${transferType}` : '';
    return this.request<any>(`/providers/zenus/counterparties${queryString}`, {
      method: 'GET'
    });
  }

  // Get a specific counterparty
  async getCounterparty(id: string): Promise<any> {
    return this.request<any>(`/providers/zenus/counterparties/${id}`, {
      method: 'GET'
    });
  }

  // Update a counterparty
  async updateCounterparty(id: string, data: {
    nickname?: string;
    beneficiaryName?: string;
    personType?: 'P' | 'L';
    givenName?: string;
    surname?: string;
    accountNumber?: string;
    accountCountryCode?: string;
    routingNumber?: string;
    swiftCode?: string;
    addressStreet1?: string;
    addressStreet2?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    addressCountry?: string;
    identificationType?: string;
    identificationValue?: string;
    bankName?: string;
    bankAddress?: string;
    bankCity?: string;
    bankCountry?: string;
    intermediaryBankBIC?: string;
    intermediaryBankCountry?: string;
    reference?: string;
  }): Promise<any> {
    return this.request<any>(`/providers/zenus/counterparties/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  // Delete a counterparty
  async deleteCounterparty(id: string): Promise<any> {
    return this.request<any>(`/providers/zenus/counterparties/${id}`, {
      method: 'DELETE'
    });
  }

  // USDC Wallet methods
  async getUSDCAddress(): Promise<any> {
    return this.request<any>('/providers/zenus/usdc/address', {
      method: 'GET'
    });
  }

  async createUSDCWallet(): Promise<any> {
    return this.request<any>('/providers/zenus/usdc/enable', {
      method: 'POST'
    });
  }

  async getUSDCTransactions(page: number = 1, size: number = 20): Promise<any> {
    return this.request<any>(`/providers/zenus/usdc/transactions?page=${page}&size=${size}`, {
      method: 'GET'
    });
  }
}

export const zenusService = new ZenusService();